import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Radio, Button, Modal } from 'antd';
import { SubsContext } from './SubscriptionInfo';
import { gainPlan, getCardTypeName, getPlanName, losePlan, valuePlan } from './plans';

const PaymentForm = styled.form`
  display: grid;
  gap: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const CardField = styled.div`
  width: 100%;
  height: 35px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  padding: 8px;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  margin-top: 8px;
  display: ${props => props.visible ? 'block' : 'none'};
`;

const TestCardInfo = styled.div`
  display: flex;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const TestCardColumn = styled.div`
  flex: 50%;
  padding: 10px;
`;

const TestCardTitle = styled.div`
  font-weight: bold;
  margin-bottom: 8px;
  color: #666;
`;

const TestCardNumber = styled.div`
  font-family: monospace;
  font-size: 16px;
  color: #007bff;
  margin-bottom: 4px;
`;

const HelperText = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

const PaymentModal = ({
  visible,
  isLoading,
  error,
  onSubmit,
  onClose,
  customerInfo,
  selectedPlan,
}) => {
  const {
    billsbyCustomerId,
    subscription,
    billsbyCard,
    plans,
    subscriptionLevel,
    freeTrialPeriod,
    previousPlan,
  } = useContext(SubsContext);
  const [newCreditCard, setNewCreditCard] = useState(!billsbyCustomerId || subscription && subscription.status === 'Suspended');
  const [errors, setErrors] = useState('');
  const [tokenizerReady, setTokenizerReady] = useState(false);
  const [isTokenizerLoading, setIsTokenizerLoading] = useState(false);

  // const nextBillingTime = subscription ? Date.parse(subscription.nextBillingDate) : '';
  const currSubscriptionLevel = subscription ? subscription.planName.split(' - ')[1] : subscriptionLevel;
  const nextSubscriptionLevel = subscription && subscription.planIdNext ? getPlanName(subscription.planIdNext, plans).split(' - ')[1] : currSubscriptionLevel;
  const inTrial = subscription && subscription.status === 'InTrial';
  const actualSubscriptionLevel = !billsbyCustomerId || inTrial ? 'Full' : currSubscriptionLevel;

  useEffect(() => {
    setNewCreditCard(!billsbyCustomerId || subscription && subscription.status === 'Suspended')
  }, [billsbyCustomerId]);

  useEffect(() => {
    const initBillsby = async () => {
      try {
        // Set loading state for tokenizer
        setIsTokenizerLoading(true);
        setTokenizerReady(false);

        // Wait for billsbyTokens to be available
        if (!window.billsbyTokens) {
          await window.billsbyReady;
        }

        // Add debug log to check modal and card state
        console.log('PaymentModal debug - newCreditCard:', newCreditCard, 'billsbyCard:', billsbyCard);

        // Wait for DOM elements to be ready
        const waitForElements = () => new Promise((resolve) => {
          const checkElements = () => {
            const numberElement = document.getElementById('billsby-number');
            const cvvElement = document.getElementById('billsby-cvv');

            if (numberElement && cvvElement) {
              resolve();
            } else {
              setTimeout(checkElements, 100);
            }
          };
          checkElements();
        });

        await waitForElements();

        window.billsbyTokens.removeHandlers();
        window.billsbyTokens.init('billsby-number', 'billsby-cvv');

        // Add debug logs for tokenization
        window.billsbyTokens.on('ready', () => {
          console.log('Billsby tokenizer is ready');
          setTokenizerReady(true);
          setIsTokenizerLoading(false);
        });

        window.billsbyTokens.on('paymentMethod', (token, pmData) => {
          console.log('Token created:', token);
          console.log('PmData received:', pmData);
          setErrors('');
          onSubmit(pmData);
          setNewCreditCard(false);
        });

        window.billsbyTokens.on('errors', (tokenizationErrors) => {
          console.error('Billsby tokenization errors:', tokenizationErrors);
          if (tokenizationErrors && tokenizationErrors.length > 0) {
            const errorMessages = tokenizationErrors.map((err) => err.message);
            setErrors(errorMessages.join(', '));
          } else {
            setErrors('Unknown error occurred during payment processing');
          }
        });
      } catch (initError) {
        console.error('Failed to initialize Billsby:', initError);
        setErrors('Failed to initialize payment system. Please try again.');
        setIsTokenizerLoading(false);
      }
    };

    if (visible && newCreditCard) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        initBillsby();
      }, 100);
    }

    return () => {
      if (window.billsbyTokens) {
        window.billsbyTokens.removeHandlers();
      }
    };
  }, [visible, onSubmit, newCreditCard]);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    // Check if we need to tokenize a new credit card
    if (newCreditCard) {
      console.log('Using tokenization for new credit card');

      // Ensure we have the month and year values
      const monthElement = document.getElementById('month');
      const yearElement = document.getElementById('year');

      const month = monthElement ? monthElement.value : null;
      const year = yearElement ? yearElement.value : null;

      if (!month || !year) {
        setErrors('Please select expiration month and year');
        return;
      }

      let fullName = 'Card Holder';
      if (customerInfo && customerInfo.firstName && customerInfo.lastName) {
        fullName = `${customerInfo.firstName} ${customerInfo.lastName}`;
      }

      // Use the tokenizer for new cards
      window.billsbyTokens.tokenizeCreditCard({
        full_name: fullName,
        month,
        year,
      });

      return;
    }

    // This path is only for existing cards (resubscribe with saved card)
    console.log('Using direct submission with existing card');
    onSubmit(null);

    // Force modal close after submission
    setTimeout(() => {
      if (onClose) {
        console.log('Closing modal after direct submission');
        onClose();
      }
    }, 100);
  };

  return (
    <Modal
      width={700}
      title="Payment Information"
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={isLoading}
        >
          {billsbyCustomerId ? 'Confirm Change' : 'Confirm Subscription'}
        </Button>,
      ]}
    >
      {selectedPlan && plans && (selectedPlan.previousPlan || previousPlan || selectedPlan.name !== nextSubscriptionLevel) && (
        <div>
          {selectedPlan.previousPlan && (
            <h2>Confirm Cancellation</h2>
          )}
          {!selectedPlan.previousPlan && (<span>
            {previousPlan && (
              <h2>Confirm Reinstatement of {selectedPlan.name}</h2>
            )}
            {!previousPlan && (
              <h2>Confirm Subscription {billsbyCustomerId ? ' Change' : ''} to {selectedPlan.name}</h2>
            )}
          </span>)}
          {!billsbyCustomerId && (<p>
            Upon acceptance of your credit card, you will begin a {freeTrialPeriod} <b>Free Trial</b> period, during which you will
            have <b>Full</b> access to all the features of ICON.  At the end of the trial period,&nbsp;
            {selectedPlan.name !== 'Full' && (<span>
              you will switch to <b>{selectedPlan.name}</b>, and&nbsp;
            </span>)}
            {selectedPlan.name === 'Full' && (<span>
              you will&nbsp;
            </span>)}
            {selectedPlan.price === 'FREE' && (<span>
              be charged nothing.
            </span>)}
            {selectedPlan.price !== 'FREE' && (<span>
              be charged <b>{selectedPlan.price}</b>.
            </span>)}
          </p>)}
          {billsbyCustomerId && (
            <div>
              {inTrial && (<p>
                You are still in your {freeTrialPeriod} <b>Free Trial</b> period and will have <b>Full</b> access until the first billing cycle, after which time&nbsp;
                {selectedPlan.name !== 'Full' && (<span>
                  you will switch to <b>{selectedPlan.name}</b>, and&nbsp;
                </span>)}
                {selectedPlan.name === 'Full' && (<span>
                  you will&nbsp;
                </span>)}
                {selectedPlan.price === 'FREE' && (<span>
                  be charged nothing.
                </span>)}
                {selectedPlan.price !== 'FREE' && (<span>
                  be charged <b>{selectedPlan.price}</b>.
                </span>)}
              </p>)}
              {!inTrial && (
                <div>
                  {valuePlan(selectedPlan.name) < valuePlan(currSubscriptionLevel) && (<div>
                    {selectedPlan.price === 'FREE' && (<p>
                      You are switching from <b>{currSubscriptionLevel}</b> to <b>{selectedPlan.name}</b>
                      {selectedPlan.name !== nextSubscriptionLevel && currSubscriptionLevel !== nextSubscriptionLevel ? ` (instead of ${nextSubscriptionLevel})` : ''},
                      which is free, beginning at the next billing cycle.  Your credit
                      card will remain on file, but will not be charged.
                    </p>)}
                    {selectedPlan.price !== 'FREE' && (<p>
                      You are switching from <b>{currSubscriptionLevel}</b> to <b>{selectedPlan.name}</b>
                      {selectedPlan.name !== nextSubscriptionLevel && currSubscriptionLevel !== nextSubscriptionLevel ? ` (instead of ${nextSubscriptionLevel})` : ''},
                      beginning at the next billing cycle, at which time
                      you will be charged <b>{selectedPlan.price}</b>.
                    </p>)}
                  </div>)}
                  {valuePlan(selectedPlan.name) > valuePlan(currSubscriptionLevel) && (
                    <p>
                      You will be switching from <b>{currSubscriptionLevel}</b> to <b>{selectedPlan.name}</b>
                      {selectedPlan.name !== nextSubscriptionLevel && currSubscriptionLevel !== nextSubscriptionLevel ? ` (instead of ${nextSubscriptionLevel})` : ''},
                      which is <b>{selectedPlan.price}</b> as soon as you confirm.
                      You will be charged for the pro-rated difference in price.
                    </p>
                  )}
                  {valuePlan(selectedPlan.name) === valuePlan(currSubscriptionLevel) && (
                    <p>
                      You will be remaining at <b>{selectedPlan.name}</b>
                      {selectedPlan.name !== nextSubscriptionLevel && currSubscriptionLevel !== nextSubscriptionLevel ? ` (instead of switching to ${nextSubscriptionLevel})` : ''},
                      beginning at the next billing cycle, and
                      you will still be charged <b>{selectedPlan.price}</b>.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          {valuePlan(actualSubscriptionLevel) > valuePlan(selectedPlan.name) && (<div>
            You will then lose the following features:
            <ul>
              {losePlan(actualSubscriptionLevel, selectedPlan.name).map(item => (
                <li>{item}</li>
              ))}
            </ul>
          </div>)}
          {valuePlan(actualSubscriptionLevel) < valuePlan(selectedPlan.name) && (<div>
            You will gain the following features:
            <ul>
              {gainPlan(actualSubscriptionLevel, selectedPlan.name).map(item => (
                <li>{item}</li>
              ))}
            </ul>
          </div>)}

        </div>
      )}
      <PaymentForm>
        {/* Loading overlay for tokenizer initialization */}
        {isTokenizerLoading && !tokenizerReady && newCreditCard && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '10px'
            }} />
            <div>Initializing payment system...</div>
            <div style={{ fontSize: '12px', marginTop: '5px' }}>Please wait a moment</div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}
        <h3>Payment Information</h3>
        <div style={{ display: (billsbyCustomerId && (!subscription || subscription.status !== 'Suspended') ? 'block' : 'none') }}>
          <Radio.Group
            onChange={e => setNewCreditCard(e.target.value)}
            value={!!newCreditCard}
          >
            <Radio value={false}>
              Use Previous Credit Card
              {billsbyCard && (
                <span style={{ marginLeft: '10px', color: '#666' }}>
                  ({getCardTypeName(billsbyCard.cardType)} ending in: {billsbyCard.cardLast4Digits} - Expires {billsbyCard.cardExpiryMonth}/{billsbyCard.cardExpiryYear})
                </span>
              )}
            </Radio>
            <br />
            <Radio value>Use New Credit Card</Radio>
          </Radio.Group>
        </div>
        <div hidden={!newCreditCard}>
          <TestCardInfo>
            <TestCardColumn>
              <TestCardTitle>Valid Test Cards</TestCardTitle>
              <TestCardNumber>4111 1111 1111 1111</TestCardNumber>
              <TestCardNumber>3782 8224 6310 005 </TestCardNumber>
              <TestCardNumber>5555 5555 5555 4444</TestCardNumber>
            </TestCardColumn>
            <TestCardColumn>
              <TestCardTitle>Declined Test Cards</TestCardTitle>
              <TestCardNumber>4012 8888 8888 1881</TestCardNumber>
              <TestCardNumber>3714 4963 5398 431 </TestCardNumber>
              <TestCardNumber>5105 1051 0510 5100</TestCardNumber>
            </TestCardColumn>
          </TestCardInfo>

          <FormGroup>
            <Label>Credit Card Number</Label>
            <CardField id="billsby-number" />
          </FormGroup>

          <FormGroup>
            <Label>Expiration Date</Label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <Input
                type="text"
                id="month"
                placeholder="MM"
                maxLength="2"
                style={{ width: '60px' }}
                pattern="[0-9]*"
                inputMode="numeric"
                required
                defaultValue={new Date().getMonth() + 1 < 10 ? `0${new Date().getMonth() + 1}` : new Date().getMonth() + 1}
              />
              <Input
                type="text"
                id="year"
                placeholder="YYYY"
                maxLength="4"
                style={{ width: '80px' }}
                pattern="[0-9]*"
                inputMode="numeric"
                required
                defaultValue={new Date().getFullYear() + 1}
              />
            </div>
          </FormGroup>

          <FormGroup>
            <Label>CVV</Label>
            <CardField id="billsby-cvv" style={{ width: '60px' }} />
            <HelperText>Enter any 3 digits (e.g., 123)</HelperText>
          </FormGroup>

          <ErrorMessage visible={!!error}>{error}</ErrorMessage>
          <ErrorMessage visible={!!errors}>{errors}</ErrorMessage>
        </div>
      </PaymentForm>
    </Modal >
  );
};

PaymentModal.propTypes = {
  visible: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  customerInfo: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
  }).isRequired,
  selectedPlan: PropTypes.shape({
    name: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    userTypeName: PropTypes.string.isRequired,
  }),
};

PaymentModal.defaultProps = {
  visible: false,
  error: PropTypes.string.isRequired,
  selectedPlan: null,
};

export default PaymentModal;



// WEBPACK FOOTER //
// ./src/components/Billsby/PaymentModal.jsx